import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { PiCalendarBlankDuotone, PiMagnifyingGlass, PiXBold } from 'react-icons/pi';
import { getColorConfigByIndex } from 'utils/StyleUtils';
import PageHeading from 'elements/PageHeading';

const isActiveHighlightedBlog = (blog, now) => {
    if (blog.highlightedBlog !== true) return false;

    const highlightedFrom = new Date(blog.highlightedBlogFrom).getTime();
    const highlightedTo = new Date(blog.highlightedBlogTo).getTime();

    return Number.isFinite(highlightedFrom)
        && Number.isFinite(highlightedTo)
        && highlightedFrom <= now
        && now <= highlightedTo;
};

export default function BlogPage(props) {
    const [list, setList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    // const [firstResult, setFirstResult] = useState(0); 
    // const [hasMore, setHasMore] = useState(true);
    const latestRequestId = useRef(0);

    const fetchBlogs = useCallback((keys = null) => {
        const requestId = ++latestRequestId.current;

        setIsLoading(true);
        const url = global.backingsEndpoint + 'landingPageControl/getBlogListByKeys';
        const body = { firstResult: 0, maxResults: 9999, keys }; 

        axios
            .post(url, body)
            .then((response) => {
                if (requestId === latestRequestId.current && response.data) {
                    // setList((prevList) => (append ? [...prevList, ...response.data] : response.data));
                    //  setHasMore(response.data.length === 10); 
                    const now = Date.now();
                    const sortedBlogs = [...response.data].sort((a, b) => {
                        const highlightDiff = Number(isActiveHighlightedBlog(b, now)) - Number(isActiveHighlightedBlog(a, now));

                        if (highlightDiff !== 0) return highlightDiff;

                        const dateDiff = new Date(b.publicationTime) - new Date(a.publicationTime);

                        if (dateDiff !== 0) return dateDiff;

                        return b.id - a.id;
                    });

                    setList(sortedBlogs);
                }
            })
            .catch((error) => {
                if (requestId === latestRequestId.current) {
                    console.error('Blog search failed:', error);
                    setList([]);
                }
            })
            .finally(() => {
                if (requestId === latestRequestId.current) {
                    setIsLoading(false);
                }
            });
    }, []);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 450);

        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        const keys = debouncedSearchTerm
            ? debouncedSearchTerm.split(/\s+/)
            : null;

        fetchBlogs(keys);
    }, [debouncedSearchTerm, fetchBlogs]);

    // const handleLoadMore = () => {
    //     const nextResult = firstResult + 10;
    //     setFirstResult(nextResult); 
    //     fetchBlogs(searchTerm.length >= 3 ? searchTerm.split(' ') : null, true); 
    // };  

    const columnCount =
        window.innerWidth >= 1024
            ? 3
            : window.innerWidth >= 768
                ? 2
                : 1;

    const blogColumns = Array.from({ length: columnCount }, () => []);

    list.forEach((blog, index) => {
        blogColumns[index % columnCount].push(blog);
    });

    return (
        <div className={'w-full flex-1 flex flex-col items-center gradient-yellow px-site py-site relative ' + (props.className || '')}>

            <div className='absolute w-full h-full top-0 left-0 bg-cover bg-center bg-no-repeat bg-fixed' style={{ backgroundImage: 'url(/assets/images/background/pattern-opacity.png)' }} />

            <div className='z-10 w-full max-w-boxed flex flex-col gap-8 md:gap-16'>

                <div className='w-full flex flex-col gap-6 md:flex-row md:items-center md:gap-10'>
                    <div className='w-full md:w-auto md:shrink-0'>
                        <PageHeading
                            title='Fürge Diák Blog'
                            align='left'
                            mobileAlign='center'
                            showSeparator={false}
                        />
                    </div>

                    <div className="relative w-full md:max-w-[720px] md:ml-auto">
                        <div className="flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 p-2 shadow-lg">
                            <div className="min-w-0 flex-1 flex items-center gap-3 rounded-xl bg-white px-4 h-14">
                                <PiMagnifyingGlass className="text-xl text-orange-500 shrink-0" />

                                <input
                                    type="search"
                                    autoComplete="off"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder="Cím, téma vagy kulcsszó..."
                                    aria-label="Keresés a blogbejegyzések között"
                                    className="min-w-0 w-full bg-transparent outline-none text-neutral-800 placeholder:text-neutral-400 text-sm md:text-base"
                                />

                                {searchTerm && (
                                    <button
                                        type="button"
                                        aria-label="Keresés törlése"
                                        title="Keresés törlése"
                                        onClick={() => setSearchTerm('')}
                                        className="size-8 rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 transition shrink-0"
                                    >
                                        <PiXBold className="m-auto text-sm" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="absolute right-2 top-full pt-2 text-right text-xs text-white/70" aria-live="polite">
                            {isLoading ? 'Keresés…' : `${list.length} találat`}
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="w-full h-screen flex items-center justify-center">
                        <div className="flex space-x-2">
                            <div className="w-4 h-4 gradient-red rounded-full animate-bounce"></div>
                            <div className="w-4 h-4 gradient-green rounded-full animate-bounce delay-200"></div>
                            <div className="w-4 h-4 gradient-blue rounded-full animate-bounce delay-400"></div>
                        </div>
                    </div>
                ) : list.length > 0 ? (
                    <div className='w-full flex flex-col md:flex-row gap-8'>
                        {blogColumns.map((column, columnIndex) => (
                            <div key={columnIndex} className='flex flex-col flex-1'>
                                {column.map((item, index) => (
                                    <BlogItem
                                        // className={index >= 3 ? 'hidden md:inline-block' : 'inline-block'}
                                        className='inline-block'
                                        key={item.id}
                                        data={item}
                                        index={index}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className='w-full rounded-3xl bg-white/15 p-8 text-center text-white font-display text-xl'>
                        Nem találtunk a keresésnek megfelelő bejegyzést.
                    </div>
                )}

                {/* {hasMore && (
                    <div className='flex justify-center '>
                        <button
                            onClick={handleLoadMore}
                            className='w-fit bg-orange-600 hover:bg-orange-500 hover:ring-8 hover:ring-yellow-400 text-white font-bold py-2 px-4 rounded-2xl shadow-md transition duration-300'
                        >
                            További találatok
                        </button>
                    </div>
                )} */}
            </div>
        </div>
    );
}

export function BlogItem(props) {
    const photoKey = props.data.mainImage?.photo;
    const bgImage = photoKey
        ? `url(${global.backingsEndpoint}photoRepository/getImageBySize?size=ORIGINAL&key=${photoKey})`
        : 'url(/assets/images/background/blog-placeholder.png)';
        
    const getPreviewText = (html, maxLength = 220) => {
        const text = String(html || '')
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<\/p>/gi, ' ')
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        if (text.length <= maxLength) return text;

        return text.slice(0, maxLength).trim().replace(/[.,;:!?-]+$/, '') + '...';
    };

    return (
        <Link to={getBlogDetailPath(props.data)} className={'h-fit rounded-3xl shadow-2xl mb-8 transition hover:ring-8 ring-white/10 ' + (props.className || '')}>
            <div className='flex flex-col'>
                <div
                    className='aspect-video bg-cover rounded-3xl flex flex-col gap-0 items-start justify-end p-4 z-10 shadow-2xl'
                    style={{ backgroundImage: bgImage }}
                >

                    <div className='flex items-center gap-2 text-white px-3 pt-2 pb-6 rounded-t-xl bg-black/75 shadow-2xl -mb-4'>
                        <PiCalendarBlankDuotone className='text-xs' />
                        <div className='font-bold text-[10px]'>{format(new Date(props.data.publicationTime), 'yyyy. MMMM dd., EEEE', { locale: hu })}</div>
                    </div>

                    <div className={'px-4 py-2 rounded-xl items-start text-white font-display font-bold leading-snug z-10 ' + getColorConfigByIndex(props.index, 'gradient')}>
                        {props.data.hl1}
                    </div>
                </div>

                <div className='text-pretty p-6 pt-12 leading-relaxed rounded-b-3xl bg-white shadow-2xl text-xs -mt-6'>
                    {getPreviewText(props.data.lead, 240)}
                </div>
            </div>
        </Link>
    );
}

export function getBlogDetailPath(blog) {
    const prettyUrl = String(blog?.prettyUrl || '')
        .trim()
        .replace(/^\/+|\/+$/g, '');

    if (prettyUrl) return `/${prettyUrl}`;

    const id = Number(blog?.id);
    return Number.isInteger(id) && id > 0
        ? `/landing?id=${id}`
        : '/blog';
}
